import React, { useState, useEffect } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, Dimensions, Platform, Modal } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useRoute } from '@react-navigation/native';
import { colores, fuentes, layout } from '../../../styles/variablesCss';
import Storage from '../../../utils/Storage';
import { useUser } from '../../../context/UserContext';
import { BACKEND_URL } from '../../../Config/Const';
import CustomText from '../../Common/CustomText';
import SelecSuc from './SelecSuc';


const HeaderSuperior = () => {
  const { nombreUsuario, logout, codUsu } = useUser();
  const route = useRoute();

  // Verificar si estamos en la pantalla de checkout
  const isFinalizarCompra = route.name === 'CheckoutFinalizarCompra' ||
    (Platform.OS === 'web' && typeof window !== 'undefined' && window.location.pathname.includes('/checkout'));

  // Estados para el tamaño de pantalla
  const [screenWidth, setScreenWidth] = useState(Dimensions.get('window').width);
  const isSmallScreen = screenWidth < 768;
  const isExtraSmallScreen = screenWidth < 576;
  // Actualizar tamaño de pantalla cuando cambia dimensiones
  useEffect(() => {
    const updateLayout = ({ window }) => {
      setScreenWidth(window.width);
    };

    // Manejo compatible tanto con API antigua como nueva de Dimensions
    const subscription = Dimensions.addEventListener('change', updateLayout);

    // Limpieza del listener al desmontar
    return () => {
      // Para versiones modernas de React Native
      if (subscription?.remove) {
        subscription.remove();
      }
      // Compatibilidad con versiones antiguas (React Native Web)
      else if (typeof Dimensions.removeEventListener === 'function') {
        Dimensions.removeEventListener('change', updateLayout);
      }
    };
  }, []);

  // Estados para la selección de sucursal (integrados desde HeaderNegro)
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedBranchState, setSelectedBranchState] = useState(null);
  const [selectedSubBranchState, setSelectedSubBranchState] = useState(null);

  // Efecto para llamar a la API de sesión de usuario cuando el componente se monta
  useEffect(() => {
    const verificarSesion = async () => {
      try {
        // Obtener mail y codUsu del localStorage
        const userMail = await Storage.getItem('mail');
        const userCodUsu = await Storage.getItem('codUsu');

        // Verificar que existan los valores necesarios
        if (!userMail || !userCodUsu) {
          return;
        }

        // Preparar los query params
        const queryParams = new URLSearchParams({
          mail: userMail,
          codUsu: userCodUsu
        }).toString();

        // Crear opciones para el fetch
        const fetchOptions = {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
          // En entornos de desarrollo, ignorar errores de certificado
          ...(process.env.NODE_ENV !== 'production' && {
            // Esta opción solo funciona en node-fetch, no en el navegador
            // Para navegadores se debe aceptar manualmente el certificado
            agent: Platform.OS !== 'web' ? new (require('https').Agent)({ rejectUnauthorized: false }) : undefined
          })
        };

        // Realizar la llamada a la API con los parámetros en la URL
        const response = await fetch(`${BACKEND_URL}/rest/usuarios/sesion?${queryParams}`, fetchOptions);

        // Procesar la respuesta
        if (response.ok) {
          const data = await response.json();
          if (data.dsResultado.resultado[0].resultado === false) {
            //CERRAR SESION
            logout();
          }
          // Aquí puedes hacer algo con la respuesta si es necesario
        } else {
          console.error('Error al verificar la sesión:', response.status);
        }
      } catch (error) {
        console.error('Error al llamar a la API de sesión:', error);
      }
    };

    // Ejecutar la función
    verificarSesion();
  }, []);

  useEffect(() => {
    loadSavedSelections(); // Cargar selecciones de sucursal guardadas
    // El carrito se carga automáticamente a través del CartContext
  }, [nombreUsuario, codUsu]);

  // Listener para detectar cambios en la sucursal seleccionada
  useEffect(() => {
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      const handleStorageChange = (e) => {
        // Detectar cambios en las claves relacionadas con sucursal
        if (e.key === 'ciudadNom' || e.key === 'selectedSubBranch') {
          loadSavedSelections();
        }
      };

      // Listener para cambios personalizados en la misma pestaña
      const handleCustomStorageChange = (e) => {
        if (e.detail?.key === 'ciudadNom' || e.detail?.key === 'selectedSubBranch') {
          loadSavedSelections();
        }
      };

      window.addEventListener('storage', handleStorageChange);
      window.addEventListener('localStorageChange', handleCustomStorageChange);

      return () => {
        window.removeEventListener('storage', handleStorageChange);
        window.removeEventListener('localStorageChange', handleCustomStorageChange);
      };
    }
  }, []);

  // Verificar automáticamente si el usuario necesita seleccionar sucursal
  useEffect(() => {
    const verificarYAbrirModalSucursal = async () => {
      try {
        // Verificar si hay usuario autenticado
        const userCodUsu = await Storage.getItem('codUsu');

        if (userCodUsu) {
          // Usuario autenticado, verificar si tiene sucursal seleccionada
          const ciudadNom = await Storage.getItem('ciudadNom');
          const pinguino = await Storage.getItem('pinguino');

          // Si NO tiene ciudad ni sucursal seleccionada, abrir el modal automáticamente
          if (!ciudadNom && !pinguino) {
            setModalVisible(true);
          }
        }
      } catch (error) {
        console.error('Error al verificar sucursal:', error);
      }
    };

    verificarYAbrirModalSucursal();
  }, [codUsu]); // Ejecutar cuando cambie codUsu (login/logout)

  // Cargar selecciones guardadas de sucursal al inicio (integrado desde HeaderNegro)
  const loadSavedSelections = async () => {
    try {
      const savedBranch = await Storage.getItem('ciudadNom');
      const savedSubBranch = await Storage.getItem('selectedSubBranch');

      if (savedBranch) {
        setSelectedBranchState(savedBranch);
        if (savedSubBranch) {
          setSelectedSubBranchState(savedSubBranch);
        }
      }
    } catch (error) {
      console.error('Error loading saved selections:', error);
    }
  };

  // Funciones para el manejo de selección de sucursal (integradas desde HeaderNegro)
  const handleSelectSucursal = () => {
    setModalVisible(true);
  };

  const handleCloseModal = () => {
    setModalVisible(false);
  };

  const handleBranchSelection = (branch, subBranch) => {
    setSelectedBranchState(branch);
    setSelectedSubBranchState(subBranch);
    setModalVisible(false); // Cerrar el modal al seleccionar
  };

  return (
    <>
      <View style={styles.mainContainer}>
        {!isFinalizarCompra && (
          <View style={[styles.whiteSection, isExtraSmallScreen && styles.whiteSectionSmall]}>
            {/* Selector de sucursal con formato: [Ciudad] [Elegí tu sucursal] [Dirección] */}
            <View style={[styles.cityBranchRow, isExtraSmallScreen && styles.cityBranchRowSmall]}>
              <Text style={[styles.cityText, isSmallScreen && styles.cityTextSmall]} numberOfLines={1} ellipsizeMode="tail">
                {selectedBranchState ? selectedBranchState.toUpperCase() : 'CIUDAD'}
              </Text>

              <TouchableOpacity
                style={[styles.chooseBranchPill, isSmallScreen && styles.chooseBranchPillSmall]}
                onPress={handleSelectSucursal}
                activeOpacity={0.7}
              >
                <Ionicons name="location" size={isSmallScreen ? 14 : 16} color={colores.rojo} />
                <Text style={[styles.chooseBranchPillText, isSmallScreen && styles.chooseBranchPillTextSmall]}>
                  Elegí tu sucursal
                </Text>
              </TouchableOpacity>

              <Text style={[styles.branchAddressTextInline, isSmallScreen && styles.branchAddressTextInlineSmall]} numberOfLines={1} ellipsizeMode="tail">
                {selectedSubBranchState ? selectedSubBranchState : ''}
              </Text>
            </View>

            {/* Iconos sociales a la derecha */}
            <View style={styles.socialRow}>
              <TouchableOpacity
                style={styles.socialIcon}
                activeOpacity={0.7}
                onPress={() => {
                  if (Platform.OS === 'web' && typeof window !== 'undefined') {
                    window.open('https://www.instagram.com/', '_blank');
                  }
                }}
              >
                <Ionicons name="logo-instagram" size={16} color={'white'} />
              </TouchableOpacity>

              <TouchableOpacity
                style={styles.socialIcon}
                activeOpacity={0.7}
                onPress={() => {
                  if (Platform.OS === 'web' && typeof window !== 'undefined') {
                    window.open('https://www.facebook.com/Supermercadospinguino/', '_blank');
                  }
                }}
              >
                <Ionicons name="logo-facebook" size={16} color={'white'}  />
              </TouchableOpacity>

              <TouchableOpacity
                style={styles.socialIcon}
                activeOpacity={0.7}
                onPress={() => {
                  if (Platform.OS === 'web' && typeof window !== 'undefined') {
                    window.open('https://ar.linkedin.com/company/cormoran-s.a.', '_blank');
                  }
                }}
              >
                <Ionicons name="logo-linkedin" size={16} color={'white'} />
              </TouchableOpacity>
            </View>
          </View>
        )}
      </View>

      {/* Modal de selección de sucursal (integrado desde HeaderNegro) - FUERA del mainContainer */}
      {Platform.OS === 'web' ? (
        // En web, renderizar directamente sin usar Modal de React Native
        modalVisible && (
          <View
            style={styles.modalOverlay}
            onClick={(e) => {
              // Cerrar si se hace clic en el overlay (no en el contenido)
              if (e.target === e.currentTarget) {
                handleCloseModal();
              }
            }}
          >
            <View style={styles.modalContent}>
              <SelecSuc onClose={handleCloseModal} onSelect={handleBranchSelection} />
            </View>
          </View>
        )
      ) : (
        // En móvil, usar Modal de React Native
        <Modal
          animationType="slide"
          transparent={true}
          visible={modalVisible}
          onRequestClose={handleCloseModal}
          statusBarTranslucent={true}
          presentationStyle="overFullScreen"
        >
          <View style={styles.modalOverlay}>
            <View style={styles.modalContent}>
              <SelecSuc onClose={handleCloseModal} onSelect={handleBranchSelection} />
            </View>
          </View>
        </Modal>
      )}
    </>
  );
};

const styles = StyleSheet.create({
  mainContainer: {
    flexDirection: 'column',
    width: '100%',
  },
  whiteSection: {
    flexDirection: 'row',
    alignItems: 'center',
    height: 40,
    width: '100%',
    backgroundColor: colores.grisOscuro,
    paddingHorizontal: layout.contentPadding,
    borderBottomWidth: 1,
    borderBottomColor: colores.grisOscuro,
    justifyContent: 'space-between',
    flexWrap: 'nowrap',
    overflow: 'hidden',
    zIndex: 950,
    position: 'relative',
  },
  whiteSectionSmall: {
    paddingHorizontal: layout.contentPaddingMobile,
    height: 'auto',
    minHeight: 40,
    minWidth: 315,
  },
  branchSelectorContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    width: '24%',
    paddingRight: 10,
    minWidth: 315,
  },
  cityBranchRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-start',
    flex: 1,
    minWidth: 0, // permitir que el contenido se encoja y elipsis funcione
    gap: 12,
  },
  cityBranchRowSmall: {
    gap: 8,
  },
  socialRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginLeft: 12,
  },
  cityText: {
    fontFamily: fuentes.bold,
    color: colores.blanco,
    fontSize: 14,
  },
  cityTextSmall: {
    fontSize: 12,
  },
  branchSelectorContainerSmall: {
    width: '30%',
    minWidth: 100,
    paddingRight: 5,
  },
  chooseBranchPill: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colores.blanco,
    borderRadius: 999,
    paddingVertical: 6,
    paddingHorizontal: 12,
    borderWidth: 1,
    borderColor: '#e0e0e0',
    ...(Platform.OS === 'web' ? {
      boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
    } : {}),
  },
  chooseBranchPillSmall: {
    paddingVertical: 4,
    paddingHorizontal: 10,
  },
  chooseBranchPillText: {
    marginLeft: 6,
    color: '#777',
    fontStyle: 'italic',
    fontFamily: fuentes.medium,
    fontSize: 14,
  },
  chooseBranchPillTextSmall: {
    fontSize: 12,
  },
  socialIcon: {
    width: 28,
    height: 28,
    borderRadius: 14,
    backgroundColor: '#555',
    paddingTop: 2,
    alignItems: 'center',
    justifyContent: 'center',
    ...(Platform.OS === 'web' ? {
      
      cursor: 'pointer',
      transition: 'transform 0.15s ease',
    } : {}),
  },
  selectBranchButton: {
    backgroundColor: colores.blanco,
    borderColor: colores.rojo,
    borderWidth: 2,
    borderRadius: 8,
    paddingVertical: 4,
    paddingHorizontal: 14,
    flexDirection: 'row',
    alignItems: 'center',
    marginRight: 10,
    flexWrap: 'wrap',
    maxWidth: '100%',
    ...(Platform.OS === 'web' ? {
      boxShadow: '0 3px 6px rgba(0, 0, 0, 0.15)',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
    } : {}),
  },
  selectBranchButtonSmall: {
    paddingVertical: 6,
    paddingHorizontal: 10,
  },
  selectBranchButtonExtraSmall: {
    paddingVertical: 4,
    paddingHorizontal: 8,
    marginRight: 5,
  },
  selectBranchButtonText: {
    color: colores.rojo,
    fontSize: 14,
    fontFamily: fuentes.bold,
    marginLeft: 2,
  },
  selectBranchButtonTextSmall: {
    fontSize: 12,
  },

  selectedLocationText: {
    color: colores.textoPrincipal,
    fontSize: 13,
    width: '100%',
    marginLeft: 5,
    marginTop: 3,
    fontFamily: fuentes.semiBold,
  },
  selectedLocationTextSmall: {
    fontSize: 11,
  },
  selectedLocationTextExtraSmall: {
    fontSize: 10,
    marginTop: 2,
    marginLeft: 2,
    width: '100%',
  },
  branchAddressTextInline: {
    color: colores.blanco,
    fontFamily: fuentes.medium,
    fontSize: 14,
    flexShrink: 1,
  },
  branchAddressTextInlineSmall: {
    fontSize: 12,
  },
  iconContainer: {
    backgroundColor: 'rgba(255, 0, 0, 0.08)',
    borderRadius: 50,
    width: 24,
    height: 24,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 5,
    flexShrink: 0,
  },
  modalOverlay: {
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    ...(Platform.OS === 'web' ? {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      width: '100vw',
      height: '100vh',
      zIndex: 999999,
      display: 'flex',
    } : {
      flex: 1,
      zIndex: 10002,
    }),
  },
  modalContent: {
    backgroundColor: colores.blanco,
    width: '90%',
    maxWidth: 600,
    maxHeight: '80%',
    borderRadius: 10,
    overflow: 'hidden',
    ...(Platform.OS === 'web' ? {
      position: 'relative',
      zIndex: 1000000,
    } : {
      zIndex: 10003,
    }),
  },
});

export default HeaderSuperior;